import React, { useState, useMemo } from "react";
import { View, TextInput, StyleSheet, Platform } from "react-native";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { colors, radii, spacing, typographyScale } from "../../../../styles/design-system";
import CustomDropdown, { CustomDropdownOption } from "../../../common/general/CustomDropDown/CustomDropdown";

interface FilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  typeFilter: string;
  onTypeFilterChange: (value: string) => void;
  severityFilter: string;
  onSeverityFilterChange: (value: string) => void;
  courseFilter: string;
  onCourseFilterChange: (value: string) => void;
  assignedFilter: string;
  onAssignedFilterChange: (value: string) => void;
}

export function FilterBar({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  typeFilter,
  onTypeFilterChange,
  severityFilter,
  onSeverityFilterChange,
  courseFilter,
  onCourseFilterChange,
  assignedFilter,
  onAssignedFilterChange,
}: FilterBarProps) {
  // Search focus state
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Dropdown open states
  const [statusOpen, setStatusOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [severityOpen, setSeverityOpen] = useState(false);
  const [courseOpen, setCourseOpen] = useState(false);
  const [assignedOpen, setAssignedOpen] = useState(false);

  // Status options
  const statusOptions: CustomDropdownOption[] = useMemo(
    () => [
      { label: "All Status", value: "all" },
      { label: "Open", value: "open" },
      { label: "In Progress", value: "in-progress" },
      { label: "Escalated", value: "escalated" },
      { label: "Resolved", value: "resolved" },
    ],
    []
  );

  // Type options
  const typeOptions: CustomDropdownOption[] = useMemo(
    () => [
      { label: "All Types", value: "all" },
      { label: "Safety Alerts", value: "safety-alert" },
      { label: "Support Requests", value: "support-request" },
    ],
    []
  );

  // Severity options
  const severityOptions: CustomDropdownOption[] = useMemo(
    () => [
      { label: "All Severity", value: "all" },
      { label: "Critical", value: "critical" },
      { label: "High", value: "high" },
      { label: "Medium", value: "medium" },
      { label: "Low", value: "low" },
    ],
    []
  );

  // Assigned options
  const assignedOptions: CustomDropdownOption[] = useMemo(
    () => [
      { label: "All Assignments", value: "all" },
      { label: "Unassigned", value: "unassigned" },
      { label: "Assigned to Me", value: "me" },
    ],
    []
  );

  return (
    <View style={styles.container}>
      {/* Search */}
      <View style={[styles.searchContainer, isSearchFocused && styles.searchContainerFocused]}>
        <MagnifyingGlass
          size={16}
          color={isSearchFocused ? colors.blue[500] : colors.grey[400]}
          style={styles.searchIcon}
        />
        <TextInput
          style={[
            styles.searchInput,
            Platform.select({
              web: {
                outlineStyle: "none",
              } as unknown,
            }),
          ]}
          placeholder="Search by student, course, or content..."
          placeholderTextColor={colors.grey[400]}
          value={searchTerm}
          onChangeText={onSearchChange}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
        />
      </View>

      {/* Filter Dropdowns */}
      <View style={[styles.filterWrapper, statusOpen && styles.filterWrapperOpen]}>
        <CustomDropdown
          options={statusOptions}
          value={statusFilter}
          onChange={onStatusFilterChange}
          placeholder="All Status"
          fullWidth
          accessibilityLabel="Filter by status"
          open={statusOpen}
          onOpenChange={setStatusOpen}
          dropdownStyle={styles.dropdownStyle}
          dropdownContainerStyle={styles.dropdownContainerStyle}
        />
      </View>

      <View style={[styles.filterWrapper, typeOpen && styles.filterWrapperOpen]}>
        <CustomDropdown
          options={typeOptions}
          value={typeFilter}
          onChange={onTypeFilterChange}
          placeholder="All Types"
          fullWidth
          accessibilityLabel="Filter by type"
          open={typeOpen}
          onOpenChange={setTypeOpen}
          dropdownStyle={styles.dropdownStyle}
          dropdownContainerStyle={styles.dropdownContainerStyle}
        />
      </View>

      <View style={[styles.filterWrapper, severityOpen && styles.filterWrapperOpen]}>
        <CustomDropdown
          options={severityOptions}
          value={severityFilter}
          onChange={onSeverityFilterChange}
          placeholder="All Severity"
          fullWidth
          accessibilityLabel="Filter by severity"
          open={severityOpen}
          onOpenChange={setSeverityOpen}
          dropdownStyle={styles.dropdownStyle}
          dropdownContainerStyle={styles.dropdownContainerStyle}
        />
      </View>

      <View style={[styles.filterWrapper, courseOpen && styles.filterWrapperOpen]}>
        <CustomDropdown
          options={[{ label: "All Courses", value: "all" }]}
          value={courseFilter}
          onChange={onCourseFilterChange}
          placeholder="All Courses"
          fullWidth
          accessibilityLabel="Filter by course"
          open={courseOpen}
          onOpenChange={setCourseOpen}
          dropdownStyle={styles.dropdownStyle}
          dropdownContainerStyle={styles.dropdownContainerStyle}
        />
      </View>

      <View style={[styles.filterWrapper, assignedOpen && styles.filterWrapperOpen]}>
        <CustomDropdown
          options={assignedOptions}
          value={assignedFilter}
          onChange={onAssignedFilterChange}
          placeholder="All Assignments"
          fullWidth
          accessibilityLabel="Filter by assignment"
          open={assignedOpen}
          onOpenChange={setAssignedOpen}
          dropdownStyle={styles.dropdownStyle}
          dropdownContainerStyle={styles.dropdownContainerStyle}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    padding: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    gap: spacing.sm,
    position: "relative",
    zIndex: 100,
    overflow: "visible",
  },
  searchContainer: {
    flex: 2,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    paddingHorizontal: spacing.sm,
    minWidth: 200,
    height: spacing["2xl"],
  },
  searchContainerFocused: {
    borderColor: colors.blue[600],
    borderWidth: 2,
  },
  searchIcon: {
    marginRight: spacing.xs,
  },
  searchInput: {
    flex: 1,
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[900],
  },
  filterWrapper: {
    flex: 1,
    minWidth: 100,
    position: "relative",
    zIndex: 1,
    overflow: "visible",
  },
  filterWrapperOpen: {
    zIndex: 5000,
    overflow: "visible",
  },
  dropdownStyle: {
    backgroundColor: colors.white,
    borderColor: colors.grey[200],
    borderRadius: radii.md,
    height: spacing["2xl"],
    minHeight: spacing["2xl"],
  },
  dropdownContainerStyle: {
    backgroundColor: colors.white,
    borderColor: colors.grey[200],
    borderRadius: radii.md,
  },
});
